import './SharePrincipal.scss';
import { ContextualMenu } from 'office-ui-fabric-react/lib/ContextualMenu';
import { DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { Persona, PersonaInitialsColor, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { ISharingPrincipal, SharingRole, ISharingStore, ClientId } from '../../../interfaces/SharingInterfaces';
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import * as ClientIdHelper from '../../../utilities/ClientIdHelper';
import stringsResx from '../../Share.resx';
import Features from '@ms/odsp-utilities/lib/features/Features';

// Features
const ReviewPermissionUI = { ODB: 772 };

export interface ISharingEntityDetailProps {
    principal: ISharingPrincipal;
    takeFocus?: boolean;
    userRole?: SharingRole;
    clientId: ClientId;
    appmap?: string;
}

export interface ISharingEntityDetailState {
    showContextualMenu?: boolean;
    target?: HTMLDivElement;
    // These fields are used to ensure correct screen reader behavior when updating role
    isUpdatingRole?: boolean;
    roleText?: string;
    previousRoleText?: string;
}

export class SharePrincipal extends React.Component<ISharingEntityDetailProps, ISharingEntityDetailState> {
    private _store: ISharingStore;
    private _focusElement: HTMLElement;

    static contextTypes = {
        sharingStore: PropTypes.object.isRequired
    };

    constructor(props: ISharingEntityDetailProps, context: any) {
        super(props);

        this.state = {
            showContextualMenu: false,
            isUpdatingRole: false,
            roleText: this._computeInitialRoleText(props.principal)
        };

        this._store = context.sharingStore;

        this._onChangeRoleClick = this._onChangeRoleClick.bind(this);
        this._onPermissionsClick = this._onPermissionsClick.bind(this);
    }

    public componentDidMount() {
        if (this._focusElement && this.props.takeFocus && !ClientIdHelper.isMobile(this.props.clientId)) {
            this._focusElement.focus();
        }
    }

    public componentDidUpdate() {
        const newRoleText = this._computeInitialRoleText(this.props.principal);
        // Checking that roleText does not equal previousRoleText so if componentDidUpdate fires before
        // updatePermissions is complete the roleText doesn't toggle back and forth
        if (newRoleText !== this.state.previousRoleText) {
            this.setState({ isUpdatingRole: false, roleText: newRoleText, previousRoleText: this.state.roleText });
        }
    }

    public render(): React.ReactElement<{}> {
        const props = this.props;
        const principal = props.principal;

        return (
            <div
                className='od-SharePrincipal'
                role='presentation'
                ref={ (element) => { this._focusElement = element; } } >
                <Persona
                    className='od-SharePrincipal-persona'
                    hidePersonaDetails={ true }
                    initialsColor={ PersonaInitialsColor.blue }
                    primaryText={ principal.primaryText }
                    size={ PersonaSize.regular }
                    imageUrl={ principal.imageUrl }
                />
                <div className='od-SharePrincipal-details' role='presentation'>
                    <div className='od-SharePrincipal-details-primaryText'>{ principal.primaryText }</div>
                    { this._renderChangePermissionsButton(principal) }
                </div>
            </div>
        );
    }

    private _computeInitialRoleText(principal: ISharingPrincipal) {
        const strings = stringsResx;
        const props = this.props;
        const role = props.principal.role;

        if (typeof role === 'number') { // SharingRole.owner === 0, so need to check type instead of truthiness.
            switch (role) {
                case SharingRole.edit:
                    return strings.canEditLabel;
                case SharingRole.review:
                    return stringsResx.canReviewRole;
                case SharingRole.view:
                    return strings.canViewLabel;
                case SharingRole.owner:
                    return strings.ownerLabel;
                default:
                    return '';
            }
        } else if (principal.sharingLinkKind) {
            return strings.accessViaSharingLink;
        } else {
            return '';
        }
    }

    private _computeRoleText() {
        if (this.state.isUpdatingRole) {
            return stringsResx.updatingPermissions;
        } else {
            return this.state.roleText;
        }
    }

    private _renderChangePermissionsButton(principal: ISharingPrincipal) {
        const role = principal.role;

        if (!this.state.isUpdatingRole && this.props.userRole === SharingRole.owner &&
            (role === SharingRole.edit || role === SharingRole.view || role === SharingRole.review)) {
            return (
                <button
                    className='od-SharePrincipal-permissions'
                    onClick={ this._onPermissionsClick }
                    data-is-focusable={ true }
                    aria-describedby={ `changePermissionsDescription${principal.id}` }
                >
                    <div className='od-SharePrincipal-permissions-role'>{ this._computeRoleText() }</div>
                    { this._renderChevron(principal.role) }
                    { this._renderContextualMenu(principal.role) }
                    <div role='tooltip' aria-hidden='true' className='od-Share-screenReaderOnly' id={ `changePermissionsDescription${principal.id}` }>{ 'Set permissions for ' + principal.primaryText }</div>
                </button >
            );
        } else {
            return (
                <div className='od-SharePrincipal-permissions-role'>{ this._computeRoleText() }</div>
            );
        }
    }

    private _renderChevron(role: SharingRole) {
        if (role === SharingRole.edit || role === SharingRole.view || role === SharingRole.review) {
            return (
                <div className='od-SharePrincipal-permissions-icon'>
                    <Icon iconName='ChevronDown' />
                </div>
            );
        }
    }

    private _renderContextualMenu(role: SharingRole): JSX.Element {
        const props = this.props;
        const strings = stringsResx;
        const showReview = Features.isFeatureEnabled(ReviewPermissionUI);
        const doesClientSupportReview = (props.clientId === ClientId.word) || (props.appmap && props.appmap === 'ms-word');
        let role1: SharingRole;
        let string1: string;
        let role2: SharingRole;
        let string2: string;
        switch (role) {
            case SharingRole.view:
                role1 = SharingRole.edit;
                string1 = stringsResx.allowEdit;
                role2 = SharingRole.review;
                string2 = stringsResx.ReviewOnly;
                break;
            case SharingRole.review:
                role1 = SharingRole.edit;
                string1 = stringsResx.allowEdit;
                role2 = SharingRole.view;
                string2 = stringsResx.changeToViewOnly;
                break;
            case SharingRole.edit:
                role1 = SharingRole.view;
                string1 = stringsResx.changeToViewOnly;
                role2 = SharingRole.review;
                string2 = stringsResx.ReviewOnly;
                break;
        }
        if (this.state.showContextualMenu && showReview && doesClientSupportReview) {
            return (
                < ContextualMenu
                    items={
                        [
                            {
                                key: 'editable1',
                                name: string1,
                                onClick: this._onChangeRoleClick.bind(this, role1)
                            },
                            {

                                key: 'editable2',
                                name: string2,
                                onClick: this._onChangeRoleClick.bind(this, role2)
                            },
                            {

                                key: 'stopSharing',
                                name: `${strings.stopSharing}`,
                                onClick: this._onStopSharingClick.bind(this)
                            }
                        ]
                    }
                    onDismiss={ () => { this.setState({ showContextualMenu: false }); } }
                    directionalHint={ DirectionalHint.bottomLeftEdge }
                    isBeakVisible={ true }
                    target={ this.state.target }
                />
            );
        } else if (this.state.showContextualMenu) {
            return (
                < ContextualMenu
                    items={
                        [
                            {
                                key: 'editable1',
                                name: string1,
                                onClick: this._onChangeRoleClick.bind(this, role1)
                            },
                            {

                                key: 'stopSharing',
                                name: `${strings.stopSharing}`,
                                onClick: this._onStopSharingClick.bind(this)
                            }
                        ]
                    }
                    onDismiss={ () => { this.setState({ showContextualMenu: false }); } }
                    directionalHint={ DirectionalHint.bottomLeftEdge }
                    isBeakVisible={ true }
                    target={ this.state.target }
                />
            );
        }
    }

    private _onChangeRoleClick(newRole: SharingRole): void {
        this.setState({
            isUpdatingRole: true,
            previousRoleText: this.state.roleText
        });
        this._store.updatePermissions(this.props.principal, newRole);
    }

    private _onStopSharingClick(ev: React.MouseEvent<{}>): void {
        this.setState({
            isUpdatingRole: true
        });
        this._store.updatePermissions(this.props.principal, SharingRole.none);
    }

    private _onPermissionsClick(ev: React.MouseEvent<any>): void {
        const role = this.props.principal.role;
        const isEditable = role === SharingRole.edit || role === SharingRole.view || role === SharingRole.review;

        if (isEditable) {
            this.setState({
                showContextualMenu: true,
                target: ev.nativeEvent.target as HTMLDivElement
            });
        }
    }
}